'use client';

import { HTMLMotionProps, motion } from 'framer-motion';
import React from 'react';

// Tách style
const animationStyles = { willChange: 'transform, opacity' as const } satisfies React.CSSProperties;

// === CẤU HÌNH BIẾN THỂ ===
type Direction = 'up' | 'down' | 'left' | 'right' | 'scale' | 'fade';

interface VariantConfig {
  initial: Record<string, number>;
  animate: Record<string, number>;
}

const variants: Record<Direction, VariantConfig> = {
  up: { initial: { y: 50, opacity: 0 }, animate: { y: 0, opacity: 1 } },
  down: { initial: { y: -50, opacity: 0 }, animate: { y: 0, opacity: 1 } },
  left: { initial: { x: -50, opacity: 0 }, animate: { x: 0, opacity: 1 } },
  right: { initial: { x: 50, opacity: 0 }, animate: { x: 0, opacity: 1 } },
  scale: { initial: { scale: 0.9, opacity: 0 }, animate: { scale: 1, opacity: 1 } },
  fade: { initial: { opacity: 0 }, animate: { opacity: 1 } },
};

// === PROPS ===
type ElementAnimationProps = HTMLMotionProps<'div'> & {
  direction?: Direction;
  distance?: number; // px hoặc scale
  delay?: number;
  duration?: number;
  ease?: 'easeOut' | 'easeIn' | 'easeInOut' | 'linear' | 'anticipate' | 'circIn';
  staggerChildren?: number; // Dùng khi là parent
};

const ElementAnimation = React.memo(
  ({
    children,
    direction = 'up',
    distance,
    delay = 0,
    duration = 0.7,
    ease = 'easeOut',
    staggerChildren,
    ...props
  }: ElementAnimationProps) => {
    // Tính toán initial/animate dựa trên direction + distance
    const base = variants[direction];
    const customInitial = { ...base.initial };
    const customAnimate = { ...base.animate };

    // Áp dụng distance tùy chỉnh
    if (distance !== undefined) {
      if (direction === 'up') customInitial.y = distance;
      if (direction === 'down') customInitial.y = -distance;
      if (direction === 'left') customInitial.x = -distance;
      if (direction === 'right') customInitial.x = distance;
      if (direction === 'scale') {
        customInitial.scale = 1 - distance / 100;
        customAnimate.scale = 1;
      }
    }

    return (
      <motion.div
        initial={customInitial}
        whileInView={customAnimate}
        viewport={{ once: true, margin: '-70px' }}
        transition={{
          duration,
          delay,
          ease,
          ...(staggerChildren && {
            staggerChildren,
            delayChildren: delay,
          }),
        }}
        style={animationStyles}
        {...props}
      >
        {children}
      </motion.div>
    );
  }
);

ElementAnimation.displayName = 'ElementAnimation';

export default ElementAnimation;
